import type { CurrentUser, ReceiveMode, RoomFilter } from "@catmail/shared";
import { Cat, MessageCircle, Moon, Plus, User, Users } from "lucide-react";
import type { ComponentType } from "react";

import { Avatar, Badge, Button } from "@/components";
import { cn } from "@/lib/cn";
import { FOCUS_ON_DARK, PRESS_FLAT } from "@/lib/interaction";

import { SelfProfilePopover, type PushControl } from "../SelfProfilePopover";

type TabDef = {
  filter: RoomFilter;
  label: string;
  Icon: ComponentType<{ className?: string }>;
};

/** 조용한 방은 나머지와 성격이 달라 구분선 아래에 따로 둔다. */
const MAIN_TABS: TabDef[] = [
  { filter: "all", label: "전체", Icon: MessageCircle },
  { filter: "direct", label: "1:1 대화", Icon: User },
  { filter: "group", label: "여러 명", Icon: Users },
];

const QUIET_TAB: TabDef = { filter: "quiet", label: "조용한 방", Icon: Moon };

type RoomRailProps = {
  user: CurrentUser;
  filter: RoomFilter;
  /** 탭마다 오른쪽에 붙는 안 읽음 개수 */
  counts: Record<RoomFilter, number>;
  onFilterChange: (filter: RoomFilter) => void;
  onNewChat: () => void;
  onSignOut?: () => void;
  onChangeReceiveMode?: (mode: ReceiveMode) => void;
  onRename?: () => void;
  onChangeAvatar?: () => void;
  onChangePassword?: () => void;
  onAddAddress?: () => void;
  /** 주소 하나를 손보는 창. 팝오버가 그대로 넘겨받는다 */
  onOpenAddress?: (identityId: string) => void;
  /** 지금 쓰는 주소 = 지금 계정 */
  currentIdentityId?: string | null;
  /**
   * 지금 쓰는 주소. 머리에 적힌다.
   *
   * `currentIdentityId`로 목록에서 찾아 쓸 수도 있지만, 어느 주소를 쓰고 있는지
   * 정하는 곳은 `useCurrentIdentity` 하나여야 한다 — 여기서 다시 고르면 그쪽이
   * 기다리는 주소를 걸러낸 결과와 어긋날 수 있다.
   */
  address?: string | null;
  /** 주소별 안 읽음 */
  unreadByIdentity?: Record<string, number>;
  onSelectIdentity?: (identityId: string) => void;
  /** 최고 관리자에게만 */
  onOpenAdmin?: (() => void) | undefined;
  /** 이 기기 알림. 팝오버가 그대로 넘겨받는다 */
  push?: PushControl;
  /** 사용자가 끌어서 정한 폭(px). 기본값은 usePanelWidths가 들고 있다 */
  width: number;
  /** 배치만 — 좁은 화면에서는 숨기고 MobileNav가 대신한다 */
  className?: string;
};

/** 시안 좌측 다크 레일 */
export function RoomRail({
  user,
  filter,
  counts,
  onFilterChange,
  onNewChat,
  onSignOut,
  onChangeReceiveMode,
  onRename,
  onChangeAvatar,
  onChangePassword,
  onAddAddress,
  onOpenAddress,
  currentIdentityId,
  address,
  unreadByIdentity,
  onSelectIdentity,
  onOpenAdmin,
  push,
  width,
  className,
}: RoomRailProps) {
  /*
   * 머리에는 **앱 이름이 아니라 지금 쓰는 주소**를 적는다.
   *
   * 앱 이름은 하루에도 몇 번씩 갈아타는 주소와 달리 한 번도 바뀌지 않아서,
   * 늘 켜져 있는 화면에서 아무것도 알려주지 않는다. 반대로 주소는 지금 무엇이
   * 오고 무엇으로 나가는지를 정하는 값이다(architecture.md §1).
   *
   * 도메인은 떼고 아이디만 남긴다. 레일이 176px이라 `master@smokat.cc`가
   * 통째로는 안 들어가고, 전체 주소는 바로 아래 내 프로필 줄에 이미 적혀 있다.
   */
  const localPart = address ? address.split("@")[0] : null;
  /*
   * 이 주소가 이 서버의 것인가.
   *
   * `addresses`에 있는 것은 전부 Cat Mail 주소다 — 그래서 지금은 늘 고양이가
   * 뜬다. `@`는 나중에 붙일 **바깥 메일 계정**(Gmail 같은 것)의 자리다. 그때는
   * 그 계정이 이 목록에 없으므로 여기가 저절로 갈린다.
   */
  const internal = user.addresses.some((entry) => entry.address === address);

  return (
    <nav
      aria-label="채팅방 분류"
      style={{ width }}
      className={cn("bg-ink flex shrink-0 flex-col pt-4.5 pb-3.5", className)}
    >
      <div className="flex items-center gap-2 px-3.5 pb-4">
        <span className="bg-paper text-ink flex size-6.5 shrink-0 items-center justify-center rounded-[9px] text-[13px] font-bold">
          {internal ? <Cat className="size-4" /> : "@"}
        </span>
        {/* 아직 주소를 못 읽었으면 앱 이름으로 버틴다. 빈 칸이 남으면 고장으로 읽힌다 */}
        <span className="text-paper truncate text-sm font-semibold tracking-tight">
          {localPart ?? "Cat Mail"}
        </span>
      </div>

      <Button
        variant="onDark"
        size="xs"
        block
        onClick={onNewChat}
        className="mx-2.75 mb-3.5 w-auto"
      >
        <Plus className="size-3.5" />새 채팅
      </Button>

      <div className="flex flex-col gap-px px-2">
        {MAIN_TABS.map((tab) => (
          <RailTab
            key={tab.filter}
            tab={tab}
            active={filter === tab.filter}
            count={counts[tab.filter]}
            onSelect={onFilterChange}
          />
        ))}

        <div className="bg-paper/12 mx-2.5 my-2 h-px" />

        <RailTab
          tab={QUIET_TAB}
          active={filter === QUIET_TAB.filter}
          count={counts.quiet}
          onSelect={onFilterChange}
        />
      </div>

      <SelfProfilePopover
        user={user}
        onSignOut={onSignOut}
        onChangeReceiveMode={onChangeReceiveMode}
        onRename={onRename}
        onChangeAvatar={onChangeAvatar}
        onChangePassword={onChangePassword}
        onAddAddress={onAddAddress}
        onOpenAddress={onOpenAddress}
        currentIdentityId={currentIdentityId}
        unreadByIdentity={unreadByIdentity}
        onSelectIdentity={onSelectIdentity}
        onOpenAdmin={onOpenAdmin}
        push={push}
      >
        <button
          type="button"
          className={`border-paper/10 hover:bg-paper/8 active:bg-paper/14 mt-auto flex items-center gap-2 border-t px-3.5 pt-3.25 pb-0.5 text-left ${PRESS_FLAT} ${FOCUS_ON_DARK}`}
        >
          <Avatar
            size="xs"
            shape="circle"
            surface="dark"
            initials={user.initials}
            colorIndex={user.colorIndex}
            imageUrl={user.avatarUrl}
            dotTone="positive"
            label={user.displayName}
          />
          <span className="flex min-w-0 flex-col gap-px">
            <span className="text-paper text-xs">{user.displayName}</span>
            <span className="text-paper/45 line-clamp-1-ko text-[10px]">
              {user.primaryAddress}
            </span>
          </span>
        </button>
      </SelfProfilePopover>
    </nav>
  );
}

type RailTabProps = {
  tab: TabDef;
  active: boolean;
  count: number;
  onSelect: (filter: RoomFilter) => void;
};

function RailTab({ tab, active, count, onSelect }: RailTabProps) {
  const { Icon, label, filter } = tab;

  return (
    <button
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={() => onSelect(filter)}
      className={cn(
        `flex h-[34px] items-center gap-2.25 rounded-lg px-2.5 text-[13px] ${PRESS_FLAT} ${FOCUS_ON_DARK}`,
        active
          ? "bg-paper/14 text-paper font-semibold"
          : "text-paper/62 hover:bg-paper/8 font-medium",
      )}
    >
      <Icon className={cn("size-3.5 shrink-0", !active && "opacity-70")} />
      <span className="flex-1 text-left">{label}</span>
      <Badge
        count={count}
        size="sm"
        tone={filter === "quiet" ? "onDark" : "alert"}
      />
    </button>
  );
}
